import {
  ContentChannel,
  ContentKind,
  POST_NEEDS_TEXT_MESSAGE,
  THREAD_MAX_POSTS,
  THREAD_PLATFORM_MESSAGE,
  countPostChars,
  postTextOf,
  socialPlatformLabel,
  supportsThreads,
  threadPostsOf,
  threadPostViolations,
  threadStructureViolation,
} from '@mg/shared';
import type {
  Content,
  CreatePostContentDto,
  Post,
  PostThread,
  ReplaceContentItem,
  SocialConnection,
  SocialPlatform,
  ThreadPostProblem,
} from '@mg/shared';
import { UPLOAD_HOLD_MESSAGE } from './uploadHold';
import {
  appendMediaToGallery,
  blockFromContent,
  MEDIA_KINDS,
  mediaHeld,
  newTextBlock,
  panelRuleViolation,
  savesText,
  wouldMixMedia,
  type AttachedAsset,
  type EditorBlock,
  type MediaHeld,
} from './blocks';

/**
 * A thread in the composer: several posts published in a row, on X or Threads.
 *
 * The composer still holds ONE flat list of blocks. A thread is that list with every block carrying
 * the key of the thread post it belongs to (`threadKey`), in this normal form, which every function
 * here keeps:
 *
 *  - every block has a `threadKey`;
 *  - the blocks of one post sit together;
 *  - every post has exactly one TEXT block;
 *  - locked posts come first;
 *  - there are two or more posts, or one post that is locked.
 *
 * So a list is a thread exactly when some block has a `threadKey`, and the flat order is the thread
 * order, then the order of the blocks inside each post. A post is LOCKED when it is live on the
 * platform, or when a send of it was started and nobody knows how it ended: it can no longer change.
 *
 * Everything here is a pure function of what it is given. None of them makes a key or a block -
 * those are passed in, because a state updater runs twice under StrictMode and a key made inside one
 * would differ between the two runs. The exceptions are the blocks `appendMediaToGallery` makes for
 * added files, as it always has, and the blocks built from saved rows - by `threadFromContents`, and
 * by `applyThreadLocks` for a post that has just been locked - whose keys come from the rows' ids
 * (or from the blocks that held those rows).
 *
 * The composer page itself is mounted by no test, so what a Save sends, which ids the form adopts
 * afterwards, how a saved post is laid out and every gate on Save, Schedule and Publish are decided
 * here, where they are pinned (thread.test.ts).
 */

type Lock = NonNullable<EditorBlock['lock']>;

/** One post of a thread as the editor draws it: its blocks, in order, and what locks it. */
export interface ThreadPostView {
  /** The post's `threadKey` - and `''` for the single post of a list that is not a thread. */
  key: string;
  blocks: EditorBlock[];
  lock: Lock | null;
}

/** The lock of a post, from its blocks' locks: live when any is, else unconfirmed when any is. */
function strongest(locks: ReadonlyArray<Lock | null | undefined>): Lock | null {
  if (locks.includes('live')) return 'live';
  return locks.includes('unconfirmed') ? 'unconfirmed' : null;
}

function lockOf(blocks: readonly EditorBlock[]): Lock | null {
  return strongest(blocks.map((b) => b.lock));
}

/** Where the post `key` sits in the flat list, as [start, end), or null when no block has that key. */
function postRange(blocks: readonly EditorBlock[], key: string): [number, number] | null {
  const start = blocks.findIndex((b) => b.threadKey === key);
  if (start === -1) return null;
  let end = start + 1;
  while (end < blocks.length && blocks[end].threadKey === key) end += 1;
  return [start, end];
}

function withoutThreadKey(block: EditorBlock): EditorBlock {
  const next = { ...block };
  delete next.threadKey;
  return next;
}

function withLock(block: EditorBlock, lock: Lock | null): EditorBlock {
  if (lock) return { ...block, lock };
  const next = { ...block };
  delete next.lock;
  return next;
}

/**
 * One post that nothing locks is not a thread: its blocks give up their key and the composer is
 * back in the single-post layout. A lone LOCKED post keeps its key - it can no longer be edited, and
 * the single-post layout has no way to show that.
 */
function settled(blocks: EditorBlock[]): EditorBlock[] {
  if (!isThreadForm(blocks)) return blocks;
  const posts = threadPosts(blocks);
  return posts.length === 1 && !posts[0].lock ? blocks.map(withoutThreadKey) : blocks;
}

/** Whether the form holds a thread: some block belongs to a thread post. */
export function isThreadForm(blocks: readonly EditorBlock[]): boolean {
  return blocks.some((b) => b.threadKey != null);
}

/**
 * The posts of the form, in order: each run of consecutive blocks with one `threadKey`. A post is
 * live when any of its blocks is, else unconfirmed when any is.
 *
 * A list that is not a thread is one run, so it comes back as ONE post (under the key `''`) and a
 * caller that walks posts needs no second path for it; an empty list has no posts.
 */
export function threadPosts(blocks: readonly EditorBlock[]): ThreadPostView[] {
  const posts: ThreadPostView[] = [];
  for (const block of blocks) {
    const key = block.threadKey ?? '';
    const last = posts[posts.length - 1];
    if (last && last.key === key) last.blocks.push(block);
    else posts.push({ key, blocks: [block], lock: null });
  }
  for (const post of posts) post.lock = lockOf(post.blocks);
  return posts;
}

/**
 * Change the blocks of ONE thread post: `update` is handed that post's blocks and returns what they
 * become, and the result goes back where the post was with the post's key on every block.
 *
 * EVERY change to a thread post's blocks goes through here. It is the one guardian of "in a thread
 * every block has the key of its post": a file added by an upload, a Media Library insert or a new
 * empty media tile is a new block, and nothing else would give it the key.
 *
 * A LOCKED post comes back untouched and `update` is not called - so no control, and no late
 * callback (an upload or a library insert that finishes after the post went live), can change a
 * post that can no longer change. An unknown key returns the list as it is.
 */
export function withPostBlocks(
  blocks: EditorBlock[],
  key: string,
  update: (slice: EditorBlock[]) => EditorBlock[],
): EditorBlock[] {
  const range = postRange(blocks, key);
  if (!range) return blocks;
  const [start, end] = range;
  const slice = blocks.slice(start, end);
  if (lockOf(slice)) return blocks;
  const next = update(slice);
  // Nothing changed (an insert that was refused, a gallery that is gone): the same list, so React
  // has nothing to redraw.
  if (next === slice) return blocks;
  return [
    ...blocks.slice(0, start),
    ...next.map((b) => (b.threadKey === key ? b : { ...b, threadKey: key })),
    ...blocks.slice(end),
  ];
}

/**
 * Why this post cannot be turned into a thread yet, or null when it can. A thread post is one text
 * box and at most one media panel, and the post that is there becomes post 1 untouched - so it has
 * to be that shape first: exactly one TEXT block, with at most one run of photos and video after it
 * or before it (a post saved with its picture above its text; the arrows that could move it are
 * gone, and nothing a thread post goes through - its card, the save, the preview, the server's
 * rules, the publisher - needs the text first).
 * The reason is the "Add post to thread" button's `title` while it is disabled.
 */
export function canStartThread(blocks: readonly EditorBlock[]): string | null {
  const textAt = blocks.findIndex((b) => b.kind === ContentKind.TEXT);
  const media = [...blocks.slice(0, textAt), ...blocks.slice(textAt + 1)];
  if (
    textAt !== -1 &&
    (textAt === 0 || textAt === blocks.length - 1) &&
    media.every((b) => MEDIA_KINDS.has(b.kind))
  ) {
    return null;
  }
  if (blocks.some((b) => b.kind === ContentKind.DOCUMENT)) {
    return 'Remove the PDF to start a thread.';
  }
  if (blocks.filter((b) => b.kind === ContentKind.TEXT).length > 1) {
    return 'Remove the extra text box to start a thread.';
  }
  return 'Remove the extra media to start a thread.';
}

/**
 * Turn a single post into a thread of two. Every block there is becomes post 1 under `firstKey` -
 * keys, ids and order untouched - and `second`, a TEXT block that already carries its own
 * `threadKey`, is added after it as post 2.
 *
 * Only a post that can start a thread does ({@link canStartThread}); a list that is already a
 * thread, or one that is not the right shape, comes back as it is. That check is made HERE, on the
 * list the change is applied to, because the button that asked was drawn from an earlier one.
 */
export function startThread(
  blocks: EditorBlock[],
  firstKey: string,
  second: EditorBlock,
): EditorBlock[] {
  if (isThreadForm(blocks) || canStartThread(blocks) != null) return blocks;
  return [...blocks.map((b) => ({ ...b, threadKey: firstKey })), second];
}

/**
 * Add `post` - a TEXT block carrying its own `threadKey` - as the last post of the thread. Nothing
 * happens at {@link THREAD_MAX_POSTS} posts, or on a list that is not a thread (that is
 * {@link startThread}).
 */
export function addThreadPost(blocks: EditorBlock[], post: EditorBlock): EditorBlock[] {
  if (!isThreadForm(blocks) || threadPosts(blocks).length >= THREAD_MAX_POSTS) return blocks;
  return [...blocks, post];
}

/**
 * Swap a post with the one before it (-1) or after it (1), text and media together as one unit.
 * Nothing happens at either end of the thread, on a locked post, or when the neighbour is locked: a
 * post that is live keeps its place, and nothing moves past it.
 */
export function moveThreadPost(blocks: EditorBlock[], key: string, delta: -1 | 1): EditorBlock[] {
  const posts = threadPosts(blocks);
  const from = posts.findIndex((p) => p.key === key);
  const to = from + delta;
  if (from === -1 || to < 0 || to >= posts.length) return blocks;
  if (posts[from].lock || posts[to].lock) return blocks;
  const next = [...posts];
  [next[from], next[to]] = [next[to], next[from]];
  return next.flatMap((p) => p.blocks);
}

/**
 * Delete a post: its text and its media. Nothing happens on a locked post, and the last post left
 * is never deleted - a social post always has a text box.
 *
 * When that leaves exactly one post and nothing locks it, its blocks lose their `threadKey`: the
 * form is a single post again, holding that post's content. A lone locked post keeps its key.
 */
export function removeThreadPost(blocks: EditorBlock[], key: string): EditorBlock[] {
  const posts = threadPosts(blocks);
  const gone = posts.find((p) => p.key === key);
  if (!gone || gone.lock || posts.length < 2) return blocks;
  return settled(posts.filter((p) => p !== gone).flatMap((p) => p.blocks));
}

/**
 * A social post always has a text box: with "Add text" gone there would be no way to get one back.
 * The same list when it holds a TEXT block, else `spare` put in front of what is there (an old
 * media-only post opens with an empty box above its media).
 */
export function ensureSocialText(blocks: EditorBlock[], spare: EditorBlock): EditorBlock[] {
  return blocks.some((b) => b.kind === ContentKind.TEXT) ? blocks : [spare, ...blocks];
}

/**
 * Bring the form's locks in line with the saved post: which of its thread posts are live, and
 * which one is waiting for a decision.
 *
 * Locks follow ROWS, never positions. A block that is a stored row of a post before
 * `thread.published` is live; one of post `thread.published` is unconfirmed when the thread says
 * so; every other block that has an id loses its lock. A block with no id yet takes the lock of the
 * rest of its post: it sits in a post that can no longer change.
 *
 * A post that GAINS a lock here - it was open in the form, and has just gone out, or may have - is
 * laid out again from its saved rows, as {@link threadFromContents} lays out a locked post: what
 * went out is what was saved, so whatever the form held beside it is dropped - a file that landed
 * while it was being sent, text typed after the request had ended, a picture moved. A post that can
 * no longer change must not show what is not on the platform, and the server refuses every save
 * that changes one. Each block keeps the key of the block that held its row (a row the form had
 * lost comes back under `c-<row id>`), so nothing React draws for it is made again. Locked posts
 * then stand first, in their saved order, as on the platform: a post moved above one that went
 * live elsewhere goes back below it.
 *
 * `post.thread === undefined` means "not loaded", never "not a thread", so the list comes back as
 * it is (and likewise when the thread is given without the rows to read it against). `null` - the
 * saved post is no thread and nothing of it is live - clears every lock. A form left as one post
 * that nothing locks is a single post again, as after a delete ({@link removeThreadPost}). A list
 * that is not a thread has nothing to lock.
 */
export function applyThreadLocks(
  blocks: EditorBlock[],
  post: Pick<Post, 'contents' | 'thread'>,
): EditorBlock[] {
  const { thread, contents } = post;
  if (thread === undefined || (thread != null && contents === undefined)) return blocks;
  if (!isThreadForm(blocks)) return blocks;

  const rows = [...(contents ?? [])].sort((a, b) => a.order - b.order);
  const postOfRow = new Map(rows.map((row) => [row.id, row.threadIndex ?? 0]));
  const indexOf = (block: EditorBlock) => (block.id == null ? undefined : postOfRow.get(block.id));
  const lockOfRow = (block: EditorBlock): Lock | null => {
    const index = indexOf(block);
    if (thread == null || index === undefined) return null;
    if (index < thread.published) return 'live';
    return index === thread.published && thread.unconfirmed ? 'unconfirmed' : null;
  };

  let changed = false;
  const posts = threadPosts(blocks).map((p) => {
    const kept = p.blocks.filter((b) => b.id != null);
    const lockOfPost = strongest(kept.map(lockOfRow));
    // Which saved post this one is, when it is locked: the post of the row that locks it.
    const at = kept.find((b) => lockOfPost != null && lockOfRow(b) === lockOfPost);
    const index = at ? indexOf(at) : undefined;
    if (lockOfPost && !p.lock && index !== undefined) {
      changed = true;
      const keyOfRow = new Map(kept.map((b) => [b.id, b.key]));
      return {
        index,
        blocks: rows
          .filter((row) => (row.threadIndex ?? 0) === index)
          .map(
            (row): EditorBlock => ({
              ...blockFromContent(row),
              key: keyOfRow.get(row.id) ?? `c-${row.id}`,
              threadKey: p.key,
              lock: lockOfPost,
            }),
          ),
      };
    }
    return {
      index,
      blocks: p.blocks.map((b) => {
        const lock = b.id != null ? lockOfRow(b) : lockOfPost;
        if ((b.lock ?? null) === lock) return b;
        changed = true;
        return withLock(b, lock);
      }),
    };
  });
  const locked = posts
    .filter((p) => lockOf(p.blocks))
    .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  const ordered = [...locked, ...posts.filter((p) => !lockOf(p.blocks))];
  if (ordered.some((p, i) => p !== posts[i])) changed = true;
  return settled(changed ? ordered.flatMap((p) => p.blocks) : blocks);
}

/**
 * The form of a saved post that is a thread, or null when it is not one (the caller then lays the
 * post out as it always has).
 *
 * Rows in stored order (`order`, ties as given), grouped into posts by `threadIndex`. A post is a
 * thread when its rows belong to two or more posts, or when it is a single post the saved `thread`
 * locks - a thread cut back to its one live post is still a thread. Otherwise null.
 *
 * Keys come from the rows' ids - `c-<row id>` for a block and `t-<id of the post's first row>` for
 * its post - so laying the same saved post out twice gives the same keys, and a list React is
 * already drawing does not remount. Locks are set as {@link applyThreadLocks} sets them. A post
 * that is not locked and has no TEXT row gets an empty text box at its start (`n-<its key>`); a
 * locked one is left exactly as it was saved.
 */
export function threadFromContents(
  contents: readonly Content[],
  thread: PostThread | null | undefined,
): EditorBlock[] | null {
  const rows = [...contents].sort((a, b) => a.order - b.order);
  const groups = threadPostsOf(rows);
  if (groups.length === 1 && thread == null) return null;

  const keyed = groups.flatMap((group) => {
    const threadKey = `t-${group[0].id}`;
    return group.map((row): EditorBlock => ({
      ...blockFromContent(row),
      key: `c-${row.id}`,
      threadKey,
    }));
  });
  const locked = applyThreadLocks(keyed, { contents: rows, thread });
  if (!isThreadForm(locked)) return null;

  return threadPosts(locked).flatMap((p) =>
    p.lock || p.blocks.some((b) => b.kind === ContentKind.TEXT)
      ? p.blocks
      : [{ ...newTextBlock(), key: `n-${p.key}`, threadKey: p.key }, ...p.blocks],
  );
}

/**
 * A saved post as blocks, in stored order: a social post that is a thread laid out as one
 * ({@link threadFromContents}), every other post as it always was - one block per row. This is the
 * post AS SAVED, which the preview of a locked post is drawn from; the form opens with
 * {@link formFromSavedPost}.
 *
 * Only a social post is asked whether it is a thread: no other type can hold one.
 */
export function savedPostBlocks(
  post: Pick<Post, 'channel' | 'contents' | 'thread'>,
): EditorBlock[] {
  const rows = [...(post.contents ?? [])].sort((a, b) => a.order - b.order);
  const thread =
    post.channel === ContentChannel.SOCIAL ? threadFromContents(rows, post.thread) : null;
  return thread ?? rows.map(blockFromContent);
}

/**
 * What the form opens with for a saved post. A thread: its posts, exactly as
 * {@link threadFromContents} lays them out. Any other post: its blocks - one empty text box
 * (`spare`) when it has none, as a new post starts - and, on a social post, a text box in front when
 * it holds only media ({@link ensureSocialText}). Blog, Video and Podcast open as they always have.
 *
 * A thread is never put through that last guarantee: a text box set in front of it would belong to
 * no post, and each of its posts that can still change already has one.
 */
export function formFromSavedPost(
  post: Pick<Post, 'channel' | 'contents' | 'thread'>,
  spare: EditorBlock,
): EditorBlock[] {
  const saved = savedPostBlocks(post);
  if (isThreadForm(saved)) return saved;
  const blocks = saved.length ? saved : [spare];
  return post.channel === ContentChannel.SOCIAL ? ensureSocialText(blocks, spare) : blocks;
}

/** What one Save of the form sends, and which block each row came from. */
export interface ThreadSavePlan {
  /** The rows, in order - the body of `PUT /posts/:id/contents`. */
  contents: ReplaceContentItem[];
  /** `keys[i]` is the key of the block `contents[i]` was made from. */
  keys: string[];
}

/**
 * The rows a Save sends for the form, made in ONE walk over its posts - and, beside each row, the
 * key of the block it came from, which is how the ids the server answers with find their way back
 * ({@link adoptContentIds}). Two lists built separately would be two chances to disagree about
 * which block a row is.
 *
 * A TEXT block is always a row, empty or not: every post keeps its text box. A photo, video, audio
 * or PDF block is a row only once it holds a file - an unfilled one of ANY kind is a placeholder the
 * editor drew, never content. A row carries its `threadIndex` from the second post on.
 */
export function threadSavePlan(blocks: readonly EditorBlock[]): ThreadSavePlan {
  const contents: ReplaceContentItem[] = [];
  const keys: string[] = [];
  threadPosts(blocks).forEach((post, index) => {
    for (const block of post.blocks) {
      if (block.kind !== ContentKind.TEXT && block.assetId == null) continue;
      contents.push({
        ...(block.id != null ? { id: block.id } : {}),
        kind: block.kind,
        ...(savesText(block) ? { text: block.text } : {}),
        assetId: block.assetId,
        ...(index > 0 ? { threadIndex: index } : {}),
      });
      keys.push(block.key);
    }
  });
  return { contents, keys };
}

/**
 * The same rows for a post that does not exist yet (`POST /posts`): one per plan item, in the same
 * order, so the ids of the created rows pair with `plan.keys` exactly as a Save's do.
 */
export function createContentsFromPlan(plan: ThreadSavePlan): CreatePostContentDto[] {
  return plan.contents.map((item, order) => ({
    kind: item.kind,
    ...(item.text !== undefined ? { text: item.text } : {}),
    ...(item.assetId != null ? { assetId: item.assetId } : {}),
    order,
    ...(item.threadIndex !== undefined ? { threadIndex: item.threadIndex } : {}),
  }));
}

/**
 * Give the form the ids of the rows a Save just wrote. `sentKeys` are the plan's keys and `rows`
 * the rows the server answered with: in stored order, row `i` is the row written for plan item `i`,
 * so the block whose key is `sentKeys[i]` takes that row's id (and its picture's address).
 *
 * Paired by KEY, never by position in the form as it is NOW: the form may have changed while the
 * request was away. A block added meanwhile simply has no id yet, one that moved still gets its
 * own, and one that was deleted is not there to take any. A block that was not sent keeps its id
 * only while that row still exists - an emptied media tile's row has just been deleted.
 */
export function adoptContentIds(
  blocks: EditorBlock[],
  sentKeys: readonly string[],
  rows: readonly Content[],
): EditorBlock[] {
  const stored = [...rows].sort((a, b) => a.order - b.order);
  const rowOfKey = new Map<string, Content>();
  sentKeys.forEach((key, i) => {
    if (stored[i]) rowOfKey.set(key, stored[i]);
  });
  const ids = new Set(stored.map((row) => row.id));
  return blocks.map((block) => {
    const row = rowOfKey.get(block.key);
    if (row) return { ...block, id: row.id, assetUrl: row.assetUrl ?? block.assetUrl };
    if (block.id == null || ids.has(block.id)) return block;
    const next = { ...block };
    delete next.id;
    return next;
  });
}

/** How one Save writes the post's content, and which request goes first. */
export interface ContentSavePlan {
  /**
   * `create`: the post is created with its rows. `replace`: every row in one write
   * (`PUT /posts/:id/contents`). `rows`: row by row, as a post that is not a thread always has.
   */
  mode: 'create' | 'replace' | 'rows';
  /** Which is written first: the post itself (type, platform, account) or its content. */
  first: 'post' | 'contents';
}

/**
 * The order follows what the FORM is now. A thread: the post first, so that the platform can take
 * a thread before thread rows arrive. A single post saved over a stored thread: the content first,
 * so that the thread rows are gone before the platform or the type changes. Either order the other
 * way round is refused by the server half-way.
 */
export function contentSavePlan(a: {
  isNew: boolean;
  formIsThread: boolean;
  storedIsThread: boolean;
}): ContentSavePlan {
  if (a.isNew) return { mode: 'create', first: 'post' };
  if (a.formIsThread) return { mode: 'replace', first: 'post' };
  if (a.storedIsThread) return { mode: 'replace', first: 'contents' };
  return { mode: 'rows', first: 'post' };
}

/** Required copy when the composer blocks a save because the text is over the platform's cap. */
export const CHAR_LIMIT_MESSAGE =
  'This post exceeds the character limit for the selected platform. Please shorten the text to save changes.';

/** Required copy when a single post holds more images than its destination takes. */
export function imageLimitMessage(
  images: number,
  limit: number,
  platform: SocialPlatform | null,
): string {
  return `This post has ${images} images, more than the ${limit} ${
    platform ? socialPlatformLabel[platform] : 'this destination'
  } accepts. Remove some to save changes.`;
}

/** Every gate the composer reads before it saves, schedules, queues or publishes. */
export interface ComposerGates {
  /** The form is a thread: the per-post rules below apply, not the single-post ones. */
  thread: boolean;
  /**
   * A file is still on its way into the post (uploadHold.ts): its slot is empty until it lands.
   * Holds Save, publishing and every save made in code, said as a neutral line under Save.
   */
  uploadHold: string | null;
  /** The thread's shape cannot be stored. Refused on every save, the AI assist's included. */
  structureError: string | null;
  /** Blocks Save, in red under the button. */
  saveError: string | null;
  /** What `persist()` throws for the same state. */
  persistError: string | null;
  /** Blocks Save, as a neutral hint: a post that is still empty. */
  saveHint: string | null;
  /** Saves, but cannot be scheduled, queued or published. */
  publishWarning: string | null;
  /** Threads only: every post is already live, so Publish only records the thread as published. */
  nothingToSend: boolean;
  /** Threads only: a post's outcome is not known; nothing more goes out until a person says. */
  unconfirmedHold: string | null;
  /** Threads only: the chosen account cannot publish a thread. */
  permissionHold: string | null;
}

/**
 * The ONE source of every gate the composer reads, so that Save, the two save prompts, Schedule,
 * the queue, Publish now and `persist()` cannot disagree about the same form.
 *
 * A post that is not a thread gets the values the composer computed before threads: text over the
 * cap or too many images block Save (the red line names the images first, `persist()` the text
 * first), and the destination's panel rule blocks publishing only. So does a SOCIAL post with no
 * text: it saves, and is never sent - its title never stands in for the text it does not have.
 *
 * A thread is judged POST BY POST, by the shared rules the server applies to the same rows
 * (`threadStructureViolation`, `threadPostViolations`) over the rows a Save would send. Locked
 * posts are skipped: they cannot change, and they are not sent again. The whole-post rules - one
 * text box, one media panel, the image cap, "text in the focused box" - are not asked of a thread
 * at all: a thread of three posts with two images each would break every one of them.
 *
 * `account` is the SELECTED account and `stored` the saved post's `thread`; both are only read for
 * a thread. `uploading` - a file still on its way into the post - holds both kinds alike.
 */
export function composerGates(a: {
  blocks: readonly EditorBlock[];
  platform: SocialPlatform | null;
  channel: ContentChannel;
  charLimit: number | null;
  imageLimit: number;
  account?: Pick<SocialConnection, 'canPublishThreads' | 'threadsBlockedReason'> | null;
  stored?: PostThread | null;
  uploading?: boolean;
}): ComposerGates {
  const { blocks, platform, channel, charLimit, imageLimit, account, stored } = a;
  const uploadHold = a.uploading ? UPLOAD_HOLD_MESSAGE : null;

  if (!isThreadForm(blocks)) {
    const images = blocks.filter((b) => b.kind === ContentKind.IMAGE && b.assetId).length;
    const overImage = channel === ContentChannel.SOCIAL && images > imageLimit;
    // Counted the way the platform counts (`countPostChars`: on X a link is 23 and an emoji 2), as
    // the counter shows it and the server checks it - not the number of characters typed.
    const overChar =
      charLimit != null &&
      blocks.some(
        (b) => b.kind === ContentKind.TEXT && countPostChars(platform, b.text) > charLimit,
      );
    const tooManyImages = overImage ? imageLimitMessage(images, imageLimit, platform) : null;
    const tooLong = overChar ? CHAR_LIMIT_MESSAGE : null;
    const social = channel === ContentChannel.SOCIAL;
    // Said before the panel rule, as the server says it: a post with no text is refused before
    // anything else about it is looked at.
    const noText = social && postTextOf(blocks) == null ? POST_NEEDS_TEXT_MESSAGE : null;
    return {
      thread: false,
      uploadHold,
      structureError: null,
      saveError: tooManyImages ?? tooLong,
      persistError: tooLong ?? tooManyImages,
      saveHint: null,
      publishWarning: noText ?? panelRuleViolation([...blocks], platform, social),
      nothingToSend: false,
      unconfirmedHold: null,
      permissionHold: null,
    };
  }

  const posts = threadPosts(blocks);
  const pieces = threadSavePlan(blocks).contents;
  const violations = threadPostViolations(
    platform,
    threadPostsOf(pieces),
    posts.filter((p) => p.lock).length,
  );
  const first = (...problems: ThreadPostProblem[]) =>
    violations.find((v) => problems.includes(v.problem))?.message ?? null;

  const structureError = threadStructureViolation(platform, channel, pieces);
  const saveError = structureError ?? first('too-long', 'too-many-images');
  // The state a thread that will not be continued ends in: its unsent posts deleted, what is left
  // all live. Publishing it sends nothing and needs no account.
  const nothingToSend = posts.every((p) => p.lock === 'live');
  return {
    thread: true,
    uploadHold,
    structureError,
    saveError,
    persistError: saveError,
    saveHint: first('empty'),
    publishWarning: first('no-text', 'media'),
    nothingToSend,
    unconfirmedHold: stored?.unconfirmed ? threadUnconfirmedText(platform, stored) : null,
    // With no account chosen the composer's own "choose an account" nudge speaks instead.
    permissionHold:
      !nothingToSend && account && account.canPublishThreads !== true
        ? (account.threadsBlockedReason ?? THREAD_PLATFORM_MESSAGE)
        : null,
  };
}

/**
 * Save is held: a file is still on its way into the post, something has to be put right first (the
 * red line), or a thread post is still empty (the neutral hint). What only stops publishing does not
 * hold a Save - a post that breaks such a rule has to stay saveable, or it could never be fixed.
 */
export function isSaveBlocked(gates: ComposerGates): boolean {
  return gates.uploadHold != null || gates.saveError != null || gates.saveHint != null;
}

/**
 * Why the form cannot be scheduled, queued or published as it stands, or null when it can: a file
 * still on its way, then the first of what holds a Save, what holds publishing only, a post whose
 * outcome is not known, and an account that cannot publish a thread. It is the reason shown where
 * "Publish now" is held.
 */
export function publishHoldReason(gates: ComposerGates): string | null {
  return (
    gates.uploadHold ??
    gates.saveError ??
    gates.saveHint ??
    gates.publishWarning ??
    gates.unconfirmedHold ??
    gates.permissionHold
  );
}

/** Schedule, the queue and Publish are held - exactly when there is a reason to give. */
export function isPublishBlocked(gates: ComposerGates): boolean {
  return publishHoldReason(gates) != null;
}

/**
 * Whether "Publish now" cannot be pressed. Beside the gates it takes what only the composer knows:
 * an account that can publish is chosen (`canPublish`), and nothing is in flight (`busy`). A post
 * with no text is held by the gates themselves - a single post as a whole, a thread post by post
 * ("Post n needs text.") - so nothing here asks about a text box.
 *
 * A thread with nothing left to send needs no account: the publish only records it as published.
 */
export function isPublishNowHeld(
  gates: ComposerGates,
  a: { canPublish: boolean; busy: boolean },
): boolean {
  return (!a.canPublish && !gates.nothingToSend) || a.busy || isPublishBlocked(gates);
}

/**
 * What a save made in code refuses with, or null when it goes ahead - the buttons are disabled for
 * the same state, but three call sites do not go through a button.
 *
 * A thread whose shape cannot be stored is refused always, and so is any save while a file is still
 * on its way: what it saved would not hold the file. Everything else is waived for the one kind of
 * save that needs the post to exist before the form is ready (`allowOverLimit`: the AI assist's "save
 * first" and "Add destination"): generating a shorter draft is the way OUT of a post that is too
 * long, and generating into an empty thread post is how it gets its text.
 */
export function persistRefusal(gates: ComposerGates, allowOverLimit: boolean): string | null {
  if (gates.structureError) return gates.structureError;
  if (gates.uploadHold) return gates.uploadHold;
  return allowOverLimit ? null : (gates.persistError ?? gates.saveHint);
}

/**
 * Add files to the media of the post that holds the block `anchorKey` - the ONE insert, for an
 * upload and for the Media Library alike.
 *
 * The anchor is looked up in `prev`, the list as it is when the files arrive, not as it was when
 * they were chosen: an upload that finishes after "Add post to thread" lands in post 1 with its
 * key, and one that finishes after a delete collapsed the thread lands in the post that is left. A
 * gallery that is gone is a no-op.
 *
 * A post that is not a thread gets exactly what it got before: `opts.recheckMixing` (the Media
 * Library, which can sit open while the post changes) re-asks the rule against photos beside video
 * on a social post, and `appendMediaToGallery` enforces the image cap over the whole post. In a
 * thread both are asked of the anchor's POST alone, through {@link withPostBlocks} - so the files
 * get the post's key, and a locked post takes nothing.
 */
export function insertMediaAtAnchor(
  prev: EditorBlock[],
  anchorKey: string,
  assets: AttachedAsset[],
  imageLimit: number,
  opts: { social: boolean; recheckMixing: boolean },
): EditorBlock[] {
  const anchor = prev.find((b) => b.key === anchorKey);
  if (!anchor) return prev;
  if (anchor.threadKey == null) {
    return opts.recheckMixing && opts.social && wouldMixMedia(mediaHeld(prev), assets)
      ? prev
      : appendMediaToGallery(prev, anchorKey, assets, imageLimit);
  }
  return withPostBlocks(prev, anchor.threadKey, (slice) =>
    wouldMixMedia(mediaHeld(slice), assets)
      ? slice
      : appendMediaToGallery(slice, anchorKey, assets, imageLimit),
  );
}

/**
 * Change the block `key` - a lone video or photo given a file by its own upload ("Replace") - looked
 * up in `prev`, the list as it is when the file arrives, as {@link insertMediaAtAnchor} finds its
 * anchor: an upload that finishes after a delete collapsed the thread still lands on its block, and
 * one that finishes after its post was locked changes nothing - in a thread every change goes
 * through {@link withPostBlocks}. A block that is gone is a no-op.
 */
export function patchBlockAt(
  prev: EditorBlock[],
  key: string,
  patch: Partial<EditorBlock>,
): EditorBlock[] {
  const block = prev.find((b) => b.key === key);
  if (!block) return prev;
  const apply = (list: EditorBlock[]) => list.map((b) => (b.key === key ? { ...b, ...patch } : b));
  return block.threadKey == null ? apply(prev) : withPostBlocks(prev, block.threadKey, apply);
}

/**
 * How many more images the gallery `anchorKey` can take, and what its post already holds - over the
 * anchor's post in a thread, where the cap and the mixing rule are per post, else over the whole
 * list.
 */
export function mediaSlotFor(
  blocks: readonly EditorBlock[],
  anchorKey: string,
  imageLimit: number,
): { remaining: number; held: MediaHeld } {
  const anchor = blocks.find((b) => b.key === anchorKey);
  const range = anchor?.threadKey != null ? postRange(blocks, anchor.threadKey) : null;
  const slice = range ? blocks.slice(range[0], range[1]) : [...blocks];
  const images = slice.filter((b) => b.kind === ContentKind.IMAGE && b.assetId).length;
  return { remaining: Math.max(0, imageLimit - images), held: mediaHeld(slice) };
}

/**
 * The key of the text box the AI assist writes into, or null when there is none.
 *
 * A thread: the text box that last had focus (`focusedTextKey`), while it is still there and its
 * post is not locked; else the first text box of a post that is not locked; else none - a thread
 * whose posts are all locked takes no draft. `focusedKey` is not read: "Add media" in a card puts
 * focus on a media block, and that must not move the target to another post.
 *
 * Any other post: the rule it has always had - the block in focus when it is a text box, else the
 * first text box.
 */
export function aiTargetKey(
  blocks: readonly EditorBlock[],
  focusedKey: string | null,
  focusedTextKey: string | null,
): string | null {
  const isText = (b: EditorBlock) => b.kind === ContentKind.TEXT;
  if (!isThreadForm(blocks)) {
    return (
      (blocks.find((b) => b.key === focusedKey && isText(b)) ?? blocks.find(isText))?.key ?? null
    );
  }
  const open = threadPosts(blocks)
    .filter((p) => !p.lock)
    .flatMap((p) => p.blocks.filter(isText));
  return (open.find((b) => b.key === focusedTextKey) ?? open[0])?.key ?? null;
}

/**
 * Put a generated draft into the form: it replaces the text of the box the AI assist writes into
 * ({@link aiTargetKey}), looked up in the list as it is NOW.
 *
 * A thread with no such box - every post locked - is left as it is, and a thread never gains a block
 * here: a text box added at its end would belong to no post. Any other post gets what it always
 * got: with no text box at all, `spare` is added at its end, holding the draft.
 */
export function applyGeneratedText(
  blocks: EditorBlock[],
  body: string,
  focusedKey: string | null,
  focusedTextKey: string | null,
  spare: EditorBlock,
): EditorBlock[] {
  const target = aiTargetKey(blocks, focusedKey, focusedTextKey);
  if (target != null) return blocks.map((b) => (b.key === target ? { ...b, text: body } : b));
  return isThreadForm(blocks) ? blocks : [...blocks, { ...spare, text: body }];
}

/**
 * Why the Type select and the accounts that cannot take a thread are disabled, said beside each -
 * while the form is a thread; nothing otherwise. A thread never reaches a type or a platform that
 * has none: it is cut back to one post first, and once part of it is live not even that helps.
 */
export function threadSwitchBlock(blocks: readonly EditorBlock[]): {
  type: string | null;
  account: string | null;
} {
  if (!isThreadForm(blocks)) return { type: null, account: null };
  if (threadPosts(blocks).some((p) => p.lock)) {
    return {
      type: "Part of this thread is already live, so its type can't change.",
      account: 'Part of this thread is already live, so it has to stay on the same account.',
    };
  }
  return {
    type: 'This post is a thread. Delete posts 2 onwards to change its type.',
    account:
      'A thread can only go to X or Threads. Delete posts 2 onwards to choose another kind of account.',
  };
}

/**
 * Whether an account on `accountPlatform` may be chosen for the form. Any, for a post that is not a
 * thread. A thread: only a platform that takes threads - and once a post is locked, only the saved
 * post's own platform (`savedPlatform`): a thread with a live part cannot change platform, and the
 * server refuses another platform's account.
 */
export function threadAccountAllowed(
  blocks: readonly EditorBlock[],
  accountPlatform: SocialPlatform,
  savedPlatform: SocialPlatform | null,
): boolean {
  if (!isThreadForm(blocks)) return true;
  if (!supportsThreads(accountPlatform)) return false;
  return !threadPosts(blocks).some((p) => p.lock) || accountPlatform === savedPlatform;
}

/**
 * Whether part of the form can no longer change: a post of it is live, or waiting for a decision.
 * While that is so the account is not a field of the form any more - a choice is saved at once, so
 * the server's answer about "the same account" arrives while the person is still looking at it.
 */
export function anyPostLocked(blocks: readonly EditorBlock[]): boolean {
  return threadPosts(blocks).some((p) => p.lock != null);
}

/**
 * "Thread · 1 post", "Thread · 3 posts" - the one builder of that label, for the composer and the
 * post card.
 */
export function threadCountLabel(posts: number): string {
  return 'Thread · ' + posts + (posts === 1 ? ' post' : ' posts');
}

/**
 * The line beside "Content" while the form is a thread, or null when it is not one: how many posts,
 * that they go out together in this order, and - once part of it is live - how many already have.
 * Counted off the form, so it follows every add, delete and publish as it happens.
 */
export function threadSummaryLine(blocks: readonly EditorBlock[]): string | null {
  if (!isThreadForm(blocks)) return null;
  const posts = threadPosts(blocks);
  const live = posts.filter((p) => p.lock === 'live').length;
  return (
    `${threadCountLabel(posts.length)}, published together in this order` +
    (live > 0 ? ` · ${live} of ${posts.length} published` : '')
  );
}

/**
 * What a person is told about the post whose outcome is not known - the one builder of that
 * sentence, for the notice that asks them and for the reason Publish is held meanwhile.
 */
export function threadUnconfirmedText(
  platform: SocialPlatform | null | undefined,
  thread: PostThread,
): string {
  const where = platform ? socialPlatformLabel[platform] : 'the platform';
  return `Post ${thread.published + 1} may already be live. Check the thread on ${where}, then say what happened.`;
}

/**
 * What "Publish now" asks before it finishes a thread that has nothing left to send: every post is
 * already live, so the publish only records the thread as published.
 */
export function threadFinishConfirmText(platform: SocialPlatform | null | undefined): string {
  const where = platform ? socialPlatformLabel[platform] : 'the platform';
  return `Every post of this thread is already live on ${where}. This marks the thread as published - nothing more is sent.`;
}

/**
 * For tests: which rule of the normal form (see the top of this file) a list breaks, or null when
 * it breaks none. A list that is not a thread only has to hold no lock - a lock means "this thread
 * post cannot change", and outside a thread there is nothing to show it.
 */
export function threadInvariantViolation(blocks: readonly EditorBlock[]): string | null {
  if (new Set(blocks.map((b) => b.key)).size !== blocks.length) return 'two blocks share a key';
  if (!isThreadForm(blocks)) {
    return blocks.some((b) => b.lock) ? 'a block is locked outside a thread' : null;
  }
  if (blocks.some((b) => !b.threadKey)) return 'a block of a thread has no threadKey';
  const posts = threadPosts(blocks);
  if (new Set(posts.map((p) => p.key)).size !== posts.length) {
    return "a post's blocks are not together";
  }
  for (const [i, post] of posts.entries()) {
    const boxes = post.blocks.filter((b) => b.kind === ContentKind.TEXT).length;
    if (boxes !== 1) return `post ${i + 1} has ${boxes} text boxes`;
  }
  const firstOpen = posts.findIndex((p) => !p.lock);
  if (firstOpen !== -1 && posts.slice(firstOpen).some((p) => p.lock)) {
    return 'a locked post comes after one that is not locked';
  }
  if (posts.length === 1 && !posts[0].lock) return 'one post that nothing locks is not a thread';
  return null;
}
